package com.dualremotetest


// World model of the Battle City–style game, at the Famicom's own scale: a 208×208-px field of 13×13 blocks of
// 16 px. Terrain is kept in 4-px cells (52×52) because the original breaks brick in 4×4-px quarters; steel only
// ever changes in aligned 8×8 tiles. Plain Kotlin (Android types only for key-code constants), unit-tested on the JVM.

const val FIELD = 208
const val CELL = 4
const val CELLS = FIELD / CELL
const val TANK = 16
const val FPS = 60

enum class Dir(val dx: Int, val dy: Int) {
    UP(0, -1), LEFT(-1, 0), DOWN(0, 1), RIGHT(1, 0);

    val opposite: Dir get() = entries[(ordinal + 2) % 4]
    val isHorizontal get() = dx != 0

    companion object {
        fun ofKey(keyCode: Int): Dir? = when (keyCode) {
            Keys.KEYCODE_DPAD_UP -> UP
            Keys.KEYCODE_DPAD_LEFT -> LEFT
            Keys.KEYCODE_DPAD_DOWN -> DOWN
            Keys.KEYCODE_DPAD_RIGHT -> RIGHT
            else -> null
        }
    }
}

object T {
    const val EMPTY = 0
    const val BRICK = 1
    const val STEEL = 2
    const val WATER = 3
    const val FOREST = 4
    const val ICE = 5

    fun blocksTank(t: Int) = t == BRICK || t == STEEL || t == WATER
    fun blocksBullet(t: Int) = t == BRICK || t == STEEL
}

/** Fixed positions, as top-left corners in field pixels (the ROM lists tank centres 16 px further right/down). */
object Spots {
    /** Enemy spawn rotation: centre, right, left (cross-reference: shinima/battle-city; the ROM rotates $6A 0→1→2). */
    val ENEMY_X = intArrayOf(96, 192, 0)
    const val ENEMY_Y = 0
    val PLAYER_X = intArrayOf(64, 128)
    const val PLAYER_Y = 192
    const val HQ_X = 96
    const val HQ_Y = 192
}

class Terrain {
    val cells = ByteArray(CELLS * CELLS)
    /** Bumped on every change so navigation knows when to re-plan. */
    var version = 0
        private set
    var bricksBroken = 0
        private set
    var steelBroken = 0
        private set

    fun load(stage: Int) {
        val rows = StageData.TERRAIN[stage - 1]
        for (by in 0 until 13) for (bx in 0 until 13) {
            val n = rows[by][bx].digitToInt(16)
            for (sy in 0 until 4) for (sx in 0 until 4) {
                cells[(by * 4 + sy) * CELLS + bx * 4 + sx] = nibbleCell(n, sx, sy).toByte()
            }
        }
        // The game draws the HQ fort itself: blocks col 5-7 / row 11-12 become the standard wall around an empty eagle spot.
        for (cy in 44 until 52) for (cx in 20 until 32) cells[cy * CELLS + cx] = T.EMPTY.toByte()
        for (i in FORT_CELLS) cells[i] = T.BRICK.toByte()
        bricksBroken = 0
        steelBroken = 0
        version++
    }

    operator fun get(cx: Int, cy: Int): Int =
        if (cx < 0 || cy < 0 || cx >= CELLS || cy >= CELLS) T.STEEL else cells[cy * CELLS + cx].toInt()

    fun at(px: Int, py: Int): Int = get(px shr 2, py shr 2)

    fun set(cx: Int, cy: Int, t: Int) {
        val i = cy * CELLS + cx
        if (cells[i].toInt() == t) return
        cells[i] = t.toByte()
        version++
    }

    /** True if any cell overlapping the pixel rectangle satisfies [pred]; outside the field counts as steel. */
    inline fun any(x: Int, y: Int, w: Int, h: Int, pred: (Int) -> Boolean): Boolean {
        for (cy in (y shr 2)..((y + h - 1) shr 2)) for (cx in (x shr 2)..((x + w - 1) shr 2)) {
            if (pred(get(cx, cy))) return true
        }
        return false
    }

    fun count(x: Int, y: Int, w: Int, h: Int, type: Int): Int {
        var n = 0
        for (cy in (y shr 2)..((y + h - 1) shr 2)) for (cx in (x shr 2)..((x + w - 1) shr 2)) if (get(cx, cy) == type) n++
        return n
    }

    /**
     * True if a tank may not occupy any part of the rectangle. ROM: tanks are blocked by whole 8×8 tiles, so a brick
     * tile with any 4×4 quarter left still blocks all of it; steel tiles are whole anyway, water covers 16×16 blocks.
     */
    fun tankBlocked(x: Int, y: Int, w: Int, h: Int): Boolean {
        for (cy in (y shr 2)..((y + h - 1) shr 2)) for (cx in (x shr 2)..((x + w - 1) shr 2)) {
            val t = get(cx, cy)
            if (t == T.STEEL || t == T.WATER || t == T.BRICK) return true
            if (brickInTile(cx, cy)) return true
        }
        return false
    }

    /** Any brick quarter left in the 8×8 tile containing the cell. */
    fun brickInTile(cx: Int, cy: Int): Boolean {
        val x0 = cx and 1.inv()
        val y0 = cy and 1.inv()
        return get(x0, y0) == T.BRICK || get(x0 + 1, y0) == T.BRICK || get(x0, y0 + 1) == T.BRICK || get(x0 + 1, y0 + 1) == T.BRICK
    }

    fun brickTileIn(x: Int, y: Int, w: Int, h: Int): Boolean {
        for (cy in (y shr 2)..((y + h - 1) shr 2)) for (cx in (x shr 2)..((x + w - 1) shr 2)) if (brickInTile(cx, cy)) return true
        return false
    }

    /** ROM: a spawning tank clears its 16×16 spawn block to empty ground. Returns true if anything was removed. */
    fun clearBlock(x: Int, y: Int): Boolean {
        var changed = false
        for (cy in y / 4 until (y + 16) / 4) for (cx in x / 4 until (x + 16) / 4) {
            if (get(cx, cy) != T.EMPTY) {
                set(cx, cy, T.EMPTY)
                changed = true
            }
        }
        return changed
    }

    /** Pixel area of the rectangle that is covered by forest. */
    fun forestArea(x: Int, y: Int, w: Int, h: Int): Int {
        var a = 0
        for (cy in (y shr 2)..((y + h - 1) shr 2)) for (cx in (x shr 2)..((x + w - 1) shr 2)) {
            if (get(cx, cy) != T.FOREST) continue
            val ox = minOf(x + w, cx * 4 + 4) - maxOf(x, cx * 4)
            val oy = minOf(y + h, cy * 4 + 4) - maxOf(y, cy * 4)
            a += ox * oy
        }
        return a
    }

    /** A bullet probe hit a brick cell: the original clears exactly that 4×4 quarter. */
    fun breakBrick(cx: Int, cy: Int): Boolean {
        if (get(cx, cy) != T.BRICK) return false
        set(cx, cy, T.EMPTY)
        bricksBroken++
        return true
    }

    /** ROM: a 3-star bullet clears the whole aligned 8×8 tile it hits, brick or steel. */
    fun breakTile(cx: Int, cy: Int): Boolean {
        val t = get(cx, cy)
        if (t != T.STEEL && t != T.BRICK) return false
        val x0 = cx and 1.inv()
        val y0 = cy and 1.inv()
        for (y in y0..y0 + 1) for (x in x0..x0 + 1) {
            when (get(x, y)) {
                T.STEEL, T.BRICK -> set(x, y, T.EMPTY)
            }
        }
        if (t == T.STEEL) steelBroken++ else bricksBroken++
        return true
    }

    fun snapshotRows(): List<String> = (0 until CELLS).map { cy ->
        buildString { for (cx in 0 until CELLS) append(".BSWFI"[get(cx, cy)]) }
    }

    companion object {
        /**
         * The HQ fort the game draws around the eagle (it is not in the stage data): an 8-px brick wall over the top
         * (x 88..120, y 184..192) and down both sides (x 88..96 and 112..120, y 192..208). Geometry per the vgrichina
         * REVERSE.md notes; the same in every stage.
         */
        val FORT_CELLS: IntArray = buildList {
            for (cy in 46..47) for (cx in 22..29) add(cy * CELLS + cx)
            for (cy in 48..51) for (cx in listOf(22, 23, 28, 29)) add(cy * CELLS + cx)
        }.toIntArray()

        fun nibbleCell(n: Int, sx: Int, sy: Int): Int {
            fun half(k: Int) = when (k) {
                0 -> sx >= 2 // right half
                1 -> sy >= 2 // bottom half
                2 -> sx < 2 // left half
                else -> sy < 2 // top half
            }
            return when (n) {
                in 0..3 -> if (half(n)) T.BRICK else T.EMPTY
                4 -> T.BRICK
                in 5..8 -> if (half(n - 5)) T.STEEL else T.EMPTY
                9 -> T.STEEL
                10 -> T.WATER
                11 -> T.FOREST
                12 -> T.ICE
                else -> T.EMPTY
            }
        }
    }
}

enum class EnemyType(val label: String, val points: Int, /** quarter pixels per frame */ val speedQ: Int, val fastBullet: Boolean, val hits: Int) {
    // Speeds: cross-reference values (cattle-bity and shinima agree: 0.5 / 1.0 px per frame), not ROM-verified.
    // Bullets: per the ROM notes only the power tank fires power-1 bullets (4 px/frame); the others 2 px/frame.
    BASIC("BASIC", 100, 2, false, 1),
    FAST("FAST", 200, 4, false, 1),
    POWER("POWER", 300, 2, true, 1),
    ARMOR("ARMOR", 400, 2, false, 4);

    companion object {
        fun ofCode(c: Char) = when (c) {
            'B' -> BASIC
            'F' -> FAST
            'P' -> POWER
            'A' -> ARMOR
            else -> error("bad roster code $c")
        }
    }
}

enum class PowerUpType(val label: String) { HELMET("HELMET"), CLOCK("CLOCK"), SHOVEL("SHOVEL"), STAR("STAR"), GRENADE("GRENADE"), TANK("TANK") }

abstract class Tank {
    var x = 0
    var y = 0
    var dir = Dir.UP
    /** Sub-pixel movement accumulator in quarter pixels. */
    var moveAcc = 0
    /** Advances per pixel moved; selects the track animation frame. */
    var tread = 0
    val cx get() = x + 8
    val cy get() = y + 8
    abstract val solid: Boolean
    /** ROM: a bullet slot stays busy for 9 frames after its bullet hits something; stage frame when each slot frees. */
    val slotFreeAt = IntArray(2)

    fun overlaps(ox: Int, oy: Int, other: Tank) =
        ox < other.x + TANK && other.x < ox + TANK && oy < other.y + TANK && other.y < oy + TANK
}

enum class PlayerState { ABSENT, SPAWNING, ACTIVE, DEAD, OUT }

class PlayerTank(val index: Int) : Tank() {
    var state = PlayerState.ABSENT
    var lives = 0
    var stars = 0
    var score = 0
    /** Frames left of the spawn star, or of the wait before it. */
    var timer = 0
    /** Extra shield frames used only by tests and simulations (normal spawns use [helmetTicks], as the ROM does). */
    var spawnShield = 0
    /** Helmet in the ROM's 64-frame ticks: 3 after every spawn, 10 from the helmet power-up. */
    var helmetTicks = 0
    /**
     * Co-op revival (our addition): game frames until this player, out of lives, comes back with one tank. Counts
     * only while the stage runs (not while paused or between stages); 0 = no revival pending.
     */
    var reviveFrames = 0
    var slideLeft = 0
    var slideDir = Dir.UP
    /** Grid mode: the coordinate the current one-step move ends at (along [gridDir]); null when no step runs. */
    var gridTarget: Int? = null
    var gridDir = Dir.UP
    /** Frames during which a player hidden in forest is still "heard" by enemies after firing. */
    var revealed = 0
    var moving = false
    val stageKills = IntArray(4)
    var deaths = 0
    var respawns = 0
    var shots = 0
    var shotsRefused = 0
    var pickups = 0
    var maxStars = 0
    var revivals = 0

    val shielded get() = spawnShield > 0 || helmetTicks > 0
    val inPlay get() = state == PlayerState.ACTIVE
    override val solid get() = state == PlayerState.ACTIVE || state == PlayerState.SPAWNING
    /** Spare tanks, counted as the original HUD does (the tank on the field is not included). */
    val spares get() = when (state) {
        PlayerState.OUT, PlayerState.ABSENT -> 0
        PlayerState.DEAD -> lives
        else -> (lives - 1).coerceAtLeast(0)
    }

    fun bulletLimit() = if (stars >= 2) 2 else 1
}

enum class EnemyState { SPAWNING, ACTIVE }

class EnemyTank(val id: Int, val type: EnemyType, val ordinal: Int, var carrier: Boolean) : Tank() {
    var state = EnemyState.SPAWNING
    var timer = 0
    var hp = type.hits
    var hitFlash = 0
    val brain = EnemyBrain()
    override val solid get() = true
}

class Bullet(
    val owner: Tank,
    val fromPlayer: Boolean,
    /** Centre point, field pixels. */
    var x: Int,
    var y: Int,
    val dir: Dir,
    /** Pixels per frame: 2, or 4 for power bullets. */
    val speed: Int,
    val pierceSteel: Boolean,
) {
    var alive = true
}

enum class Boom(val length: Int) {
    // ROM timings: bullet impact 3 phases of 3 frames; tank small1-small2-small3-big1-big2-small3 at 6 frames each
    // (3 for fast enemies); a destroyed player shows about 32 frames; the eagle 39 frames.
    BULLET(9),
    TANK(36),
    FAST_TANK(18),
    PLAYER(32),
    HQ(39),
}

class Explosion(val x: Int, val y: Int, val kind: Boom) {
    var age = 0
    val length get() = kind.length
}

class PowerUp(val type: PowerUpType, val x: Int, val y: Int) {
    var age = 0
}

class ScorePopup(val x: Int, val y: Int, val points: Int, var delay: Int, val length: Int) {
    var age = 0
}
